import { useEffect, useMemo, useRef, useState } from "react";
import { getApiSlotsValidation } from "features/settings/common/apiSlotsValidation.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  GetUpgradePricesRequest,
  GetUpgradePricesResponse,
  PaymentType,
  SubscriptionPlanType,
} from "@shared/types";
import { displayResultMessage } from "utils/misc.tsx";

interface UseGetUpgradePricesOptions {
  numApiSlots: number;
  enabled?: boolean;
  planType?: SubscriptionPlanType;
  paymentType?: PaymentType.UPGRADE | PaymentType.PRORATED_SLOTS;
  includeNumClients?: boolean;
  promoCode?: string | null;
}

const useGetUpgradePrices = ({
  numApiSlots,
  enabled,
  planType,
  paymentType,
  includeNumClients = true,
  promoCode,
}: UseGetUpgradePricesOptions) => {
  const [throttledSlots, setThrottledSlots] = useState<number | undefined>(
    numApiSlots
  );
  const throttleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSlots = useRef<number | undefined>(undefined);
  const fetchedSlotsRef = useRef<Set<number>>(new Set());
  const lastUpgradePricesRef = useRef<GetUpgradePricesResponse | undefined>(
    undefined
  );

  useEffect(() => {
    if (!enabled) return;
    const { hasMinSlotsError, isInvalidStep } =
      getApiSlotsValidation(numApiSlots);
    if (hasMinSlotsError || isInvalidStep) {
      setThrottledSlots(undefined);
      return;
    }
    pendingSlots.current = numApiSlots;
    if (fetchedSlotsRef.current.has(numApiSlots)) {
      setThrottledSlots(numApiSlots);
      return;
    }
    if (!throttleTimer.current) {
      setThrottledSlots(numApiSlots);
      throttleTimer.current = setTimeout(() => {
        throttleTimer.current = null;
        if (pendingSlots.current !== undefined) {
          const { hasMinSlotsError, isInvalidStep } = getApiSlotsValidation(
            pendingSlots.current
          );
          if (!hasMinSlotsError && !isInvalidStep) {
            setThrottledSlots(pendingSlots.current);
          }
        }
      }, THROTTLE_MS);
    }
  }, [numApiSlots, enabled]);

  const { hasMinSlotsError, isInvalidStep } =
    getApiSlotsValidation(numApiSlots);
  const isValidSlots = !hasMinSlotsError && !isInvalidStep;
  const isPendingThrottle = isValidSlots && throttledSlots !== numApiSlots;

  const args = useMemo(() => {
    if (!enabled) return skipToken;
    const baseArgs: GetUpgradePricesRequest = {};
    if (includeNumClients) {
      baseArgs.number_of_clients = throttledSlots || undefined;
    }
    if (planType) {
      baseArgs.plan_type = planType;
    }
    if (paymentType) {
      baseArgs.payment_type = paymentType;
    }
    if (promoCode) {
      baseArgs.promo_code = promoCode;
    }
    return baseArgs;
  }, [
    enabled,
    includeNumClients,
    throttledSlots,
    planType,
    paymentType,
    promoCode,
  ]);

  const {
    data: upgradePricesRaw,
    currentData: upgradePricesCurrentData,
    isFetching: isFetchingUpgradePrices,
    isLoading: isLoadingUpgradePrices,
    isError,
    error,
  } = sharedLib.sharedApi.settings.useGetUpgradePricesQuery(args);

  useEffect(() => {
    if (!isError || !error) return;
    displayResultMessage(
      Promise.reject(error),
      undefined,
      "Something went wrong. Please refresh the page and try again.",
      undefined,
      undefined,
      undefined,
      false
    ).catch(() => {});
  }, [isError, error]);

  if (
    !isFetchingUpgradePrices &&
    upgradePricesCurrentData !== undefined &&
    throttledSlots !== undefined
  ) {
    fetchedSlotsRef.current.add(throttledSlots);
  }

  if (upgradePricesRaw !== undefined) {
    lastUpgradePricesRef.current = upgradePricesRaw;
  }

  const isCurrentSlotsCached =
    isValidSlots && fetchedSlotsRef.current.has(numApiSlots);

  // Use last loaded data for display so we show previous values while loading new ones
  const upgradePrices = upgradePricesRaw ?? lastUpgradePricesRef.current;

  const isUpgradePricesLoading =
    enabled &&
    (isLoadingUpgradePrices ||
      (isValidSlots &&
        (isPendingThrottle || isFetchingUpgradePrices) &&
        !isCurrentSlotsCached));

  return {
    upgradePrices,
    isUpgradePricesLoading,
  };
};

export default useGetUpgradePrices;

const THROTTLE_MS = 2000;
